import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';

import { compareByEffectiveIbanStatus, getCleanBankAccountNumber } from './accounting/bankutils';
import {
  type CalendarDate,
  addCalendarDate,
  compareCalendarDates,
  isBefore,
  isEqual,
  toCalendarDate,
} from './dateutils';
import { roundAtSecondDecimal } from './numbers';
import { isNilOrEmpty } from './objectUtils';
import { effectiveExecutionDate } from './paymentOrderUtils';
import { normalizeString } from './strings';
import {
  type BankAccount,
  BankAccountType,
  CreateOrderFailureReason,
  type CreatedPaymentOrder,
  type OpenInvoice,
  type OpenInvoiceAccount,
  OpenInvoiceAllocationState,
  type OpenInvoiceGroupTotal,
  OpenInvoiceGrouping,
  OpenInvoiceIbanState,
  OpenInvoiceListLens,
  OpenInvoiceType,
  type OrderOverride,
  type ScopeInput,
} from './types';

export type OpenInvoiceLens = 'all' | 'ready' | 'alloc' | 'iban';
export type OpenInvoiceState = 'ready' | 'verify' | 'alloc' | 'iban';
export type OpenInvoiceGroupBy = 'type' | 'state' | 'account' | 'owner' | 'none';
export type OpenInvoiceGroupKind = Exclude<OpenInvoiceGroupBy, 'none'>;
export type UnselectableReason = 'allocateFirst' | 'addIbanFirst' | 'creditNote';
export enum FundsState {
  OK = 'ok',
  SHORT = 'short',
  UNKNOWN = 'unknown',
}
export type PayDatePresetKey = 'onDueDate' | 'asap' | 'postponeWeek';
export enum RowAction {
  ALLOCATE = 'allocate',
  ADD_IBAN = 'addIban',
  CREATE = 'create',
  NONE = 'none',
}

export const OPEN_INVOICE_LENSES: OpenInvoiceLens[] = ['all', 'ready', 'alloc', 'iban'];
export const OPEN_INVOICE_STATES: OpenInvoiceState[] = ['ready', 'verify', 'alloc', 'iban'];
export const OPEN_INVOICE_GROUP_BY_OPTIONS: OpenInvoiceGroupBy[] = ['type', 'state', 'account', 'owner', 'none'];
export const OPEN_INVOICE_TYPE_ORDER: OpenInvoiceType[] = [OpenInvoiceType.SUPPLIER, OpenInvoiceType.FEE];
export const DEFAULT_OPEN_INVOICE_LENS: OpenInvoiceLens = 'all';
export const DEFAULT_OPEN_INVOICE_GROUP_BY: OpenInvoiceGroupBy = 'type';
export const UNASSIGNED_GROUP_KEY = 'unassigned';
export const POSTPONE_PRESET_DAYS = 7;
export const FAILURE_REASON_MESSAGE_PREFIX = 'enums.CreateOrderFailureReason';

export interface OpenInvoiceOverride {
  bankAccountId?: string | null;
  payDate?: string | null;
}

export type OpenInvoiceOverrides = Map<string, OpenInvoiceOverride>;

export interface FundsVerdict {
  state: FundsState;
  available: number | null;
  short: number;
}

export type PayDateBasis = { kind: 'auto'; termDays: number | null } | { kind: 'manual' };

export interface PayDatePreset {
  key: PayDatePresetKey;
  date: CalendarDate;
}

export interface OpenInvoiceGroup {
  key: string;
  kind: OpenInvoiceGroupKind | null;
  items: OpenInvoice[];
  count: number;
  total: number;
  selectableIds: string[];
}

export interface OpenInvoiceListTotals {
  totalCount: number;
  totalAmount: number;
}

type OpenInvoiceSortFields = Pick<OpenInvoice, 'dueDate' | 'id' | 'payDate'>;

export interface OpenInvoicesPreferences {
  lens: OpenInvoiceLens;
  groupBy: OpenInvoiceGroupBy;
}

type CachedOpenInvoicesPreferences = Partial<Record<keyof OpenInvoicesPreferences, string>>;

export interface EligibleAccountsSplit {
  thirdParty: OpenInvoiceAccount[];
  agency: OpenInvoiceAccount[];
}

export const DEFAULT_OPEN_INVOICES_PREFERENCES: OpenInvoicesPreferences = {
  lens: DEFAULT_OPEN_INVOICE_LENS,
  groupBy: DEFAULT_OPEN_INVOICE_GROUP_BY,
};

export const isOpenInvoiceLens = (value: string): value is OpenInvoiceLens =>
  OPEN_INVOICE_LENSES.some((lens) => lens === value);

export const isOpenInvoiceGroupBy = (value: string): value is OpenInvoiceGroupBy =>
  OPEN_INVOICE_GROUP_BY_OPTIONS.some((option) => option === value);

export const isOpenInvoiceState = (value: string): value is OpenInvoiceState =>
  OPEN_INVOICE_STATES.some((state) => state === value);

export const isOpenInvoiceType = (value: string): value is OpenInvoiceType =>
  OPEN_INVOICE_TYPE_ORDER.some((type) => type === value);

export const OPEN_INVOICE_LIST_LENS: Record<OpenInvoiceLens, OpenInvoiceListLens> = {
  all: OpenInvoiceListLens.ALL,
  ready: OpenInvoiceListLens.READY,
  alloc: OpenInvoiceListLens.ALLOC,
  iban: OpenInvoiceListLens.IBAN,
};

export const toOpenInvoiceLens = (listLens: OpenInvoiceListLens | null | undefined): OpenInvoiceLens =>
  OPEN_INVOICE_LENSES.find((lens) => OPEN_INVOICE_LIST_LENS[lens] === listLens) ?? DEFAULT_OPEN_INVOICE_LENS;

const OPEN_INVOICE_GROUP_KINDS: OpenInvoiceGroupKind[] = ['type', 'state', 'account', 'owner'];

export const OPEN_INVOICE_GROUPING: Record<OpenInvoiceGroupKind, OpenInvoiceGrouping> = {
  type: OpenInvoiceGrouping.TYPE,
  state: OpenInvoiceGrouping.STATE,
  account: OpenInvoiceGrouping.ACCOUNT,
  owner: OpenInvoiceGrouping.OWNER,
};

export const getOpenInvoiceState = (invoice: OpenInvoice): OpenInvoiceState => {
  if (invoice.allocation === OpenInvoiceAllocationState.PENDING) {
    return 'alloc';
  }
  if (invoice.ibanState === OpenInvoiceIbanState.MISSING) {
    return 'iban';
  }
  if (invoice.ibanState === OpenInvoiceIbanState.TO_VERIFY) {
    return 'verify';
  }
  return 'ready';
};

export const isInLens = (state: OpenInvoiceState, lens: OpenInvoiceLens): boolean => {
  switch (lens) {
    case 'all':
      return true;
    case 'iban':
      return state === 'iban' || state === 'verify';
    case 'ready':
    case 'alloc':
      return state === lens;
    default:
      return false;
  }
};

export const countByLens = (items: OpenInvoice[]): Record<OpenInvoiceLens, number> =>
  items.reduce<Record<OpenInvoiceLens, number>>(
    (acc, invoice) => {
      const state = getOpenInvoiceState(invoice);
      OPEN_INVOICE_LENSES.forEach((lens) => {
        if (isInLens(state, lens)) {
          acc[lens] += 1;
        }
      });
      return acc;
    },
    { all: 0, ready: 0, alloc: 0, iban: 0 }
  );

export const isCreditNote = (invoice: OpenInvoice): boolean => invoice.amount <= 0;

const toSearchable = (value: string): string => normalizeString(value).toLowerCase();

export const getOpenInvoiceTitle = ({
  id,
  label,
  supplierName,
}: Pick<OpenInvoice, 'id' | 'label' | 'supplierName'>): string => {
  const title = [supplierName, label].filter((part) => !isNilOrEmpty(part)).join(' · ');
  return isEmpty(title) ? id : title;
};

export const matchesOpenInvoiceSearch = (
  invoice: OpenInvoice,
  accountLabel: string | null | undefined,
  search: string
): boolean => {
  const needle = toSearchable(search.trim());
  if (isEmpty(needle)) {
    return true;
  }
  const haystack = toSearchable(
    [invoice.supplierName, invoice.label, invoice.buildingName, accountLabel].filter((part) => !isNil(part)).join(' ')
  );
  return haystack.includes(needle);
};

export const getUnselectableReason = (invoice: OpenInvoice): UnselectableReason | null => {
  if (invoice.allocation === OpenInvoiceAllocationState.PENDING) {
    return 'allocateFirst';
  }
  if (invoice.ibanState === OpenInvoiceIbanState.MISSING) {
    return 'addIbanFirst';
  }
  if (isCreditNote(invoice)) {
    return 'creditNote';
  }
  return null;
};

export const isSelectableInvoice = (invoice: OpenInvoice): boolean =>
  invoice.selectable && isNil(getUnselectableReason(invoice));

export const resolveAccountId = (invoice: OpenInvoice, override?: OpenInvoiceOverride): string | null =>
  override?.bankAccountId ?? invoice.defaultBankAccountId ?? null;

export const resolvePayDate = (invoice: OpenInvoice, override?: OpenInvoiceOverride): string =>
  override?.payDate ?? invoice.payDate;

export const isReadyInvoice = (invoice: OpenInvoice, override?: OpenInvoiceOverride): boolean =>
  isSelectableInvoice(invoice) && !isNil(resolveAccountId(invoice, override));

export const getFundsVerdict = (amount: number, account: OpenInvoiceAccount | null | undefined): FundsVerdict => {
  const projected = account?.projectedBalance;
  if (isNil(projected)) {
    return { state: FundsState.UNKNOWN, available: null, short: 0 };
  }
  const available = roundAtSecondDecimal(projected - (account?.retainedBalanceThreshold ?? 0));
  if (available >= amount) {
    return { state: FundsState.OK, available, short: 0 };
  }
  return { state: FundsState.SHORT, available, short: roundAtSecondDecimal(amount - available) };
};

export const getPayDateBasis = (invoice: OpenInvoice, override?: OpenInvoiceOverride): PayDateBasis => {
  if (!isNil(override?.payDate)) {
    return { kind: 'manual' };
  }
  return { kind: 'auto', termDays: invoice.paymentTermDays ?? null };
};

export const buildPayDatePresets = (
  invoice: OpenInvoice,
  override: OpenInvoiceOverride | undefined,
  todayDate: CalendarDate
): PayDatePreset[] => [
  { key: 'onDueDate', date: effectiveExecutionDate(toCalendarDate(invoice.dueDate ?? invoice.payDate), todayDate) },
  { key: 'asap', date: todayDate },
  {
    key: 'postponeWeek',
    date: addCalendarDate(
      effectiveExecutionDate(toCalendarDate(resolvePayDate(invoice, override)), todayDate),
      POSTPONE_PRESET_DAYS,
      'days'
    ),
  },
];

export const isPayDateOverdue = (payDate: string | null | undefined, todayDate: CalendarDate): boolean =>
  !isNil(payDate) && isBefore(toCalendarDate(payDate), todayDate);

export const getRescheduledOrders = (
  orders: CreatedPaymentOrder[],
  invoices: OpenInvoice[],
  overrides: OpenInvoiceOverrides,
  todayDate: CalendarDate
): CreatedPaymentOrder[] => {
  const invoicesById = new Map(invoices.map((invoice) => [invoice.id, invoice]));
  return orders.reduce<CreatedPaymentOrder[]>((acc, order) => {
    const invoice = invoicesById.get(order.invoiceId);
    if (isNil(invoice) || isNil(order.dueDate)) {
      return acc;
    }
    const dueDate = toCalendarDate(order.dueDate);
    const payDate = toCalendarDate(resolvePayDate(invoice, overrides.get(invoice.id)));
    if (isBefore(payDate, dueDate) && isEqual(dueDate, todayDate)) {
      acc.push(order);
    }
    return acc;
  }, []);
};

const getSortDate = (invoice: OpenInvoiceSortFields): CalendarDate =>
  toCalendarDate(invoice.dueDate ?? invoice.payDate);

export const compareOpenInvoicesByDueDate = (
  invoiceA: OpenInvoiceSortFields,
  invoiceB: OpenInvoiceSortFields
): number => {
  const byDate = compareCalendarDates(getSortDate(invoiceA), getSortDate(invoiceB));
  return byDate !== 0 ? byDate : invoiceA.id.localeCompare(invoiceB.id);
};

export const sortByDueDate = (items: OpenInvoice[]): OpenInvoice[] => [...items].sort(compareOpenInvoicesByDueDate);

export const sumAmounts = (items: OpenInvoice[]): number =>
  roundAtSecondDecimal(items.reduce((acc, invoice) => acc + invoice.amount, 0));

const buildGroup = (key: string, kind: OpenInvoiceGroupKind | null, items: OpenInvoice[]): OpenInvoiceGroup => ({
  key,
  kind,
  items,
  count: items.length,
  total: sumAmounts(items),
  selectableIds: items.filter(isSelectableInvoice).map((invoice) => invoice.id),
});

export interface OpenInvoiceGroupSelection {
  checked: boolean;
  indeterminate: boolean;
  isFullyLoaded: boolean;
}

export const getGroupSelectionState = (
  { count, items, selectableIds }: OpenInvoiceGroup,
  selectedIds: Set<string>
): OpenInvoiceGroupSelection => {
  const selectedCount = selectableIds.filter((id) => selectedIds.has(id)).length;
  const isFullyLoaded = items.length >= count;
  const checked = isFullyLoaded && !isEmpty(selectableIds) && selectedCount === selectableIds.length;
  return { checked, indeterminate: selectedCount > 0 && !checked, isFullyLoaded };
};

const groupByKey = (
  items: OpenInvoice[],
  kind: OpenInvoiceGroupKind,
  getKey: (invoice: OpenInvoice) => string,
  orderedKeys?: string[]
): OpenInvoiceGroup[] => {
  const itemsByKey = new Map<string, OpenInvoice[]>();
  items.forEach((invoice) => {
    const key = getKey(invoice);
    itemsByKey.set(key, [...(itemsByKey.get(key) ?? []), invoice]);
  });
  const keys = isNil(orderedKeys) ? Array.from(itemsByKey.keys()) : orderedKeys.filter((key) => itemsByKey.has(key));
  return keys.map((key) => buildGroup(key, kind, itemsByKey.get(key) ?? []));
};

export const getOpenInvoiceGroupKey = (
  invoice: OpenInvoice,
  kind: OpenInvoiceGroupKind,
  override?: OpenInvoiceOverride
): string => {
  switch (kind) {
    case 'state':
      return getOpenInvoiceState(invoice);
    case 'account':
      return resolveAccountId(invoice, override) ?? UNASSIGNED_GROUP_KEY;
    case 'owner':
      return invoice.ownerContactId ?? UNASSIGNED_GROUP_KEY;
    case 'type':
    default:
      return invoice.type;
  }
};

export const groupOpenInvoices = (
  items: OpenInvoice[],
  groupBy: OpenInvoiceGroupBy,
  overrides: OpenInvoiceOverrides
): OpenInvoiceGroup[] => {
  switch (groupBy) {
    case 'none':
      return isEmpty(items) ? [] : [buildGroup('all', null, items)];
    case 'state':
      return groupByKey(items, 'state', getOpenInvoiceState, OPEN_INVOICE_STATES);
    case 'account':
      return groupByKey(items, 'account', (invoice) =>
        getOpenInvoiceGroupKey(invoice, 'account', overrides.get(invoice.id))
      );
    case 'owner':
      return groupByKey(items, 'owner', (invoice) => getOpenInvoiceGroupKey(invoice, 'owner'));
    case 'type':
    default:
      return groupByKey(items, 'type', (invoice) => invoice.type, OPEN_INVOICE_TYPE_ORDER);
  }
};

interface GroupTotal {
  count: number;
  amount: number;
}

const addToGroupTotal = (totals: Map<string, GroupTotal>, key: string, count: number, amount: number): void => {
  const current = totals.get(key) ?? { count: 0, amount: 0 };
  totals.set(key, { count: current.count + count, amount: roundAtSecondDecimal(current.amount + amount) });
};

export const buildOpenInvoiceGroupTotals = (items: OpenInvoice[]): OpenInvoiceGroupTotal[] =>
  OPEN_INVOICE_GROUP_KINDS.flatMap((kind) => {
    const totals = new Map<string, GroupTotal>();
    items.forEach((invoice) => addToGroupTotal(totals, getOpenInvoiceGroupKey(invoice, kind), 1, invoice.amount));
    return Array.from(totals, ([key, total]) => ({ ...total, grouping: OPEN_INVOICE_GROUPING[kind], key }));
  });

const moveOverriddenAccounts = (
  totals: Map<string, GroupTotal>,
  items: OpenInvoice[],
  overrides: OpenInvoiceOverrides
): void =>
  items.forEach((invoice) => {
    const defaultKey = getOpenInvoiceGroupKey(invoice, 'account');
    const chosenKey = getOpenInvoiceGroupKey(invoice, 'account', overrides.get(invoice.id));
    if (chosenKey !== defaultKey) {
      addToGroupTotal(totals, defaultKey, -1, -invoice.amount);
      addToGroupTotal(totals, chosenKey, 1, invoice.amount);
    }
  });

export const applyGroupTotals = (
  groups: OpenInvoiceGroup[],
  groupTotals: OpenInvoiceGroupTotal[],
  listTotals: OpenInvoiceListTotals,
  overrides: OpenInvoiceOverrides
): OpenInvoiceGroup[] => {
  const kind = groups[0]?.kind;
  if (isNil(kind)) {
    return groups.map((group) => ({ ...group, count: listTotals.totalCount, total: listTotals.totalAmount }));
  }
  const totals = groupTotals.reduce<Map<string, GroupTotal>>((acc, groupTotal) => {
    if (groupTotal.grouping === OPEN_INVOICE_GROUPING[kind]) {
      acc.set(groupTotal.key, { count: groupTotal.count, amount: groupTotal.amount });
    }
    return acc;
  }, new Map());
  if (kind === 'account') {
    moveOverriddenAccounts(
      totals,
      groups.flatMap((group) => group.items),
      overrides
    );
  }
  return groups.map((group) => {
    const total = totals.get(group.key);
    return isNil(total) ? group : { ...group, count: total.count, total: total.amount };
  });
};

export const splitEligibleAccounts = (
  invoice: OpenInvoice,
  accountsById: Map<string, OpenInvoiceAccount>
): EligibleAccountsSplit =>
  invoice.eligibleAccountIds.reduce<EligibleAccountsSplit>(
    (acc, accountId) => {
      const account = accountsById.get(accountId);
      if (isNil(account)) {
        return acc;
      }
      if (account.isAgencyAccount) {
        acc.agency.push(account);
      } else {
        acc.thirdParty.push(account);
      }
      return acc;
    },
    { thirdParty: [], agency: [] }
  );

export const supplierIbanAccounts = (
  supplierContactId: string | null | undefined,
  accounts: BankAccount[]
): BankAccount[] => {
  if (isNil(supplierContactId)) {
    return [];
  }
  return accounts
    .filter(
      (account) =>
        account.type === BankAccountType.SUPPLIER_ACCOUNT &&
        account.contactId === supplierContactId &&
        !account.disabled
    )
    .sort(compareByEffectiveIbanStatus);
};

export const pickInvoiceIbanAccount = (
  invoice: Pick<OpenInvoice, 'supplierContactId' | 'iban'>,
  accounts: BankAccount[]
): BankAccount | undefined => {
  const supplierAccounts = supplierIbanAccounts(invoice.supplierContactId, accounts);
  const { iban } = invoice;
  if (isNilOrEmpty(iban)) {
    return supplierAccounts[0];
  }
  const cleanInvoiceIban = getCleanBankAccountNumber(iban);
  return supplierAccounts.find((account) => getCleanBankAccountNumber(account.number) === cleanInvoiceIban);
};

export const getRowAction = (invoice: OpenInvoice, canWrite: boolean, canAllocate: boolean): RowAction => {
  if (!canWrite) {
    return RowAction.NONE;
  }
  switch (getUnselectableReason(invoice)) {
    case 'allocateFirst':
      return canAllocate ? RowAction.ALLOCATE : RowAction.NONE;
    case 'addIbanFirst':
      return RowAction.ADD_IBAN;
    case 'creditNote':
      return RowAction.NONE;
    default:
      return RowAction.CREATE;
  }
};

const hasOverrideValue = (override: OpenInvoiceOverride | undefined): override is OpenInvoiceOverride =>
  !isNil(override) && (!isNil(override.bankAccountId) || !isNil(override.payDate));

export const buildOrderOverrides = (invoiceIds: string[], overrides: OpenInvoiceOverrides): OrderOverride[] =>
  invoiceIds.reduce<OrderOverride[]>((acc, invoiceId) => {
    const override = overrides.get(invoiceId);
    if (hasOverrideValue(override)) {
      acc.push({ invoiceId, bankAccountId: override.bankAccountId ?? null, payDate: override.payDate ?? null });
    }
    return acc;
  }, []);

export const buildAccountOverrides = (overrides: OpenInvoiceOverrides): OrderOverride[] =>
  Array.from(overrides)
    .reduce<OrderOverride[]>((acc, [invoiceId, { bankAccountId }]) => {
      if (!isNil(bankAccountId)) {
        acc.push({ invoiceId, bankAccountId });
      }
      return acc;
    }, [])
    .sort((left, right) => left.invoiceId.localeCompare(right.invoiceId));

export const pruneSelection = (selected: Set<string>, visibleIds: Set<string>): Set<string> => {
  const kept = Array.from(selected).filter((id) => visibleIds.has(id));
  return kept.length === selected.size ? selected : new Set(kept);
};

export const getFailureReasonMessageId = (reason: CreateOrderFailureReason): string =>
  `${FAILURE_REASON_MESSAGE_PREFIX}.${reason}`;

export const serializeOverrides = (overrides: OpenInvoiceOverrides): Record<string, OpenInvoiceOverride> =>
  Object.fromEntries(overrides);

export const parseOverrides = (cached: unknown): OpenInvoiceOverrides => {
  if (isNil(cached) || typeof cached !== 'object' || Array.isArray(cached)) {
    return new Map();
  }
  return new Map(
    Object.entries(cached as Record<string, unknown>).reduce<[string, OpenInvoiceOverride][]>((acc, [id, value]) => {
      if (isNil(value) || typeof value !== 'object') {
        return acc;
      }
      const { bankAccountId, payDate } = value as Record<string, unknown>;
      const override: OpenInvoiceOverride = {
        bankAccountId: isString(bankAccountId) ? bankAccountId : null,
        payDate: isString(payDate) ? payDate : null,
      };
      if (!isNil(override.bankAccountId) || !isNil(override.payDate)) {
        acc.push([id, override]);
      }
      return acc;
    }, [])
  );
};

export const parseOpenInvoicesPreferences = (
  cached: CachedOpenInvoicesPreferences | null | undefined
): OpenInvoicesPreferences => {
  if (isNil(cached)) {
    return DEFAULT_OPEN_INVOICES_PREFERENCES;
  }
  const { lens, groupBy } = cached;
  return {
    lens: isString(lens) && isOpenInvoiceLens(lens) ? lens : DEFAULT_OPEN_INVOICE_LENS,
    groupBy: isString(groupBy) && isOpenInvoiceGroupBy(groupBy) ? groupBy : DEFAULT_OPEN_INVOICE_GROUP_BY,
  };
};

const normalizeIds = (ids: string[] | null | undefined): string[] | null =>
  isNilOrEmpty(ids) ? null : [...ids].sort((idA, idB) => idA.localeCompare(idB));

export const normalizeScope = (scope: ScopeInput): ScopeInput => ({
  managerIds: normalizeIds(scope.managerIds),
  ownerIds: normalizeIds(scope.ownerIds),
  propertyIds: normalizeIds(scope.propertyIds),
});
